import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useUpdatePaymentMethodMutation } from './graphql/UpdatePaymentMethod.generated';

/**
 * Depending on the feature flag, this will either:
 * - Update a Billing Account through monograph-svc.
 * - Update both Billing Accounts and Payment Method Card through payments-svc.
 */
export function useUpdatePaymentMethod(props: UpdatePaymentMethodProps) {
  const { onSuccess } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Payment Method Card Update ──────────────────────────────────────

  const [updatePaymentMethodCardResponse, updatePaymentMethodCardMutation] =
    useUpdatePaymentMethodMutation();

  const updatePaymentMethod = useCallback(
    async (payload: UpdatePaymentMethodPayload) => {
      const response = await updatePaymentMethodCardMutation({
        input: castPayloadToPaymentMethodCard(payload),
      });

      const result = response.data?.updatePaymentMethod;
      const isSuccess = result?.__typename === 'PaymentMethodCard';

      if (isSuccess) {
        track('payment_credit.save_card');
        addNoticeBanner({
          text: formatMessage(messages.creditCardFormUpdateSuccess),
          palette: 'success',
        });

        onSuccess();

        return;
      }

      const userError = formatMessage(messages.creditCardFormErrorSave);

      track('payment_credit.save_card_failed', { userError });
      addNoticeBanner({ text: userError, palette: 'caution' });
    },
    [
      addNoticeBanner,
      onSuccess,
      formatMessage,
      track,
      updatePaymentMethodCardMutation,
    ],
  );

  return {
    updatePaymentMethod,
    isUpdating: updatePaymentMethodCardResponse.fetching,
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormUpdateSuccess: {
    defaultMessage: 'Payment method updated successfully.',
    description: 'Credit Card Form | Update | Success',
  },
  creditCardFormErrorSave: {
    defaultMessage:
      'We were unable to save your card information. Please try again.',
    description: 'Credit Card Form | Error | Save',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UpdatePaymentMethodProps = Readonly<{
  onSuccess: () => void;
}>;

type UpdatePaymentMethodPayload = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
  paymentMethodType: 'PAYMENT_METHOD_CARD' | 'BILLING_ACCOUNT';
}>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function castPayloadToPaymentMethodCard(payload: UpdatePaymentMethodPayload) {
  return {
    id: payload.id,
    nickname: payload.nickname,
    isDefault: payload.isDefault,
  };
}
